import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const ForeignTrustEIN = () => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/diy-processes')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to DIY Processes
          </Button>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-xl bg-primary/10">
                <FileText className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Obtain Foreign Trust EIN
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Learn how to obtain an Employer Identification Number (EIN) for your foreign trust. 
              Navigate the application process efficiently and correctly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Time Estimate</h3>
              <p className="text-muted-foreground">1 hr</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Download className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Template</h3>
              <p className="text-muted-foreground">1 template included</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground mb-6">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Essential Knowledge</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Understanding EIN requirements and classifications
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Foreign trust specific application procedures
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    IRS compliance and reporting obligations
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Practical Skills</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Form completion and submission strategies
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Supporting documentation preparation
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Follow-up and confirmation procedures
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground underline">
              Start below
            </h2>
          </div>

          {/* Step Card */}
          <div className="flex justify-center">
            <div className="w-full max-w-md">
              {/* Step 1 */}
              <div className="group cursor-pointer" onClick={() => setShowVideo(true)}>
                <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                      <span className="text-xl font-bold text-primary">1</span>
                    </div>
                    <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                      Filling Out the Application
                    </h3>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                    Start
                  </Button>
                </div>
              </div>

              {/* Video Overlay */}
              {showVideo && (
                <div className="fixed inset-8 z-50 bg-black/80 flex items-center justify-center p-6 rounded-xl">
                  <Button
                    variant="outline"
                    onClick={() => setShowVideo(false)}
                    className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-background/95 hover:bg-background border-2 hover:scale-105 transition-all duration-300"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                  <div className="w-full max-w-5xl h-full relative">
                    <div style={{padding:"56.25% 0 0 0", position:"relative", width:"100%", height:"100%"}}>
                      <iframe 
                        src="https://player.vimeo.com/video/1110158356?badge=0&autopause=0&player_id=0&app_id=58479" 
                        frameBorder="0" 
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
                        referrerPolicy="strict-origin-when-cross-origin" 
                        style={{position:"absolute", top:0, left:0, width:"100%", height:"100%"}} 
                        title="How to Acquire a Foreign 98 Series EIN from IRS for your Foreign Grantor's Trust"
                      />
                    </div>
                    <script src="https://player.vimeo.com/api/player.js"></script>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ForeignTrustEIN;